import { useState, type Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { Glasses, RefreshCw } from 'lucide-react';
import {
  FRAME_LIKE_TYPES,
  ProductType,
  type ProductSummary,
  type ProductVariantSummary,
  type ValidatedBasketLine,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/LoadingState';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import { ProductSearch } from '@/modules/catalog/components/ProductSearch';
import { useProductQuery } from '@/modules/catalog/api/catalog.api';
import { useCandidateCheckQuery } from '../api/opticalOrders.api';
import {
  frameChoice,
  type BuilderAction,
  type PairDraft,
  type SelectedFrame,
} from '../builder/draft';
import { frameSizes } from '../builder/format';
import type { BuilderProblem } from '../builder/refusals';
import { Availability } from '../components/Availability';

interface FrameStepProps {
  pair: PairDraft;
  dispatch: Dispatch<BuilderAction>;
  problems: BuilderProblem[];
  showErrors: boolean;
}

/**
 * Step 2: the frame, one exact variant from the store's own stock.
 *
 * Search or scan reuses the counter's product search, limited to frames or sunglasses. Price and stock come from the server's check, as guidance: the checkout counts the stock again when the order is confirmed. A frame sent from a recommendation arrives already chosen, marked as recommended; changing it lets the recommendation go, and the step says so.
 */
export function FrameStep({ pair, dispatch, problems, showErrors }: FrameStepProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<ProductType>(ProductType.FRAME);
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const choose = (frame: SelectedFrame | null) =>
    dispatch({ type: 'setFrame', pairId: pair.id, frame });

  const frameProblem = problems.find((problem) => problem.field === 'frame');
  const dropped = pair.droppedRecommendation ? (
    <Alert tone="info">{t('opticalOrders.recommendation.noLongerApplies')}</Alert>
  ) : null;

  if (pair.frame) {
    return (
      <div className="space-y-3">
        {dropped}
        <SelectedFrameCard frame={pair.frame} />
        {frameProblem ? (
          <p className="text-sm text-red-600" role="alert">
            {t(frameProblem.message.key, frameProblem.message.values)}
          </p>
        ) : null}
        <Button variant="secondary" onClick={() => choose(null)}>
          <RefreshCw className="size-4" aria-hidden />
          {t('opticalOrders.frame.change')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {dropped}
      <FormField label={t('opticalOrders.frame.type')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={type}
            options={FRAME_LIKE_TYPES.map((value) => ({
              value,
              label: t(`catalog.types.${value}`),
            }))}
            onChange={(event) => {
              setType(event.target.value as ProductType);
              setProduct(null);
            }}
          />
        )}
      </FormField>

      {scanError ? <Alert tone="warning">{scanError}</Alert> : null}

      <ProductSearch
        productType={type}
        onSelectProduct={(selected) => {
          setScanError(null);
          setProduct(selected);
        }}
        onSelectVariant={(variant, scanned) => {
          // A barcode names one exact variant; anything that is not a frame is refused here, as the checkout would.
          if (!FRAME_LIKE_TYPES.includes(scanned.type)) {
            setScanError(t('opticalOrders.frame.notAFrame'));
            return;
          }
          setScanError(null);
          choose(frameChoice(variant, scanned, variant.sellingPriceMinor));
        }}
      />

      {product ? (
        <FrameVariantPicker
          product={product}
          onPick={(variant, price) => choose(frameChoice(variant, product, price))}
          onCancel={() => setProduct(null)}
        />
      ) : null}

      {showErrors ? (
        <p className="text-sm text-red-600" role="alert">
          {t('opticalOrders.validation.chooseFrame')}
        </p>
      ) : null}
    </div>
  );
}

function FrameVariantPicker({
  product,
  onPick,
  onCancel,
}: {
  product: ProductSummary;
  onPick: (variant: ProductVariantSummary, seenPriceMinor: number | null) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const detail = useProductQuery(product.id);
  const variants = (detail.data?.variants ?? []).filter((variant) => variant.isActive);
  const check = useCandidateCheckQuery(variants.map((variant) => variant.id));
  const lineFor = (variantId: string): ValidatedBasketLine | undefined =>
    check.data?.lines.find((line) => line.variantId === variantId);

  if (detail.isPending) return <LoadingState />;

  return (
    <section className="border-line rounded-xl border bg-white" aria-label={product.name}>
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h3 dir="auto" className="text-ink truncate text-sm font-semibold rtl:text-right">
            {product.name}
          </h3>
          <p className="text-muted text-xs">{product.brandName ?? t('catalog.fields.noBrand')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
      {variants.length === 0 ? (
        <p className="text-muted px-4 py-3 text-sm">{t('opticalOrders.frame.noVariants')}</p>
      ) : (
        <ul className="divide-line divide-y">
          {variants.map((variant) => {
            const line = lineFor(variant.id);
            const price = line?.unitPriceMinor ?? variant.sellingPriceMinor;
            const sizes = frameSizes(variant.frame);
            return (
              <li key={variant.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="text-ink text-sm font-medium">
                    {variant.variantName ?? product.name}
                  </p>
                  <p className="text-muted text-xs">
                    <span className="font-mono" dir="ltr">
                      {variant.sku}
                    </span>
                    {variant.frame?.colorName ? ` · ${variant.frame.colorName}` : ''}
                    {sizes ? (
                      <>
                        {' · '}
                        <span dir="ltr" className="font-mono">
                          {sizes}
                        </span>
                      </>
                    ) : null}
                  </p>
                  {line ? <Availability line={line} /> : null}
                </div>
                <span className="text-ink text-sm font-medium" dir="ltr">
                  {format(price)}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onPick(variant, line?.unitPriceMinor ?? variant.sellingPriceMinor)}
                  aria-label={t('opticalOrders.frame.chooseVariant', {
                    name: variant.variantName ?? product.name,
                    sku: variant.sku,
                  })}
                >
                  {t('opticalOrders.frame.choose')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The frame chosen for a pair, with what the server says about it now. */
export function SelectedFrameCard({ frame }: { frame: SelectedFrame }) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const check = useCandidateCheckQuery([frame.variantId]);
  const line = check.data?.lines.find((entry) => entry.variantId === frame.variantId);
  const sizes = frameSizes(frame.details);
  const price = line?.unitPriceMinor ?? frame.seenPriceMinor;
  const recommended = frame.recommendationToken !== null;
  // A recommendation reserves nothing: when the frame cannot be had here now, the user is told, and nothing is switched on their behalf.
  const recommendedUnavailable =
    recommended &&
    line !== undefined &&
    (line.issues.includes('INACTIVE') ||
      line.issues.includes('UNAVAILABLE') ||
      line.availableQuantity < 1);

  return (
    <section
      className="border-line flex flex-wrap items-start gap-4 rounded-xl border bg-white p-4"
      aria-label={t('opticalOrders.frame.selected')}
    >
      <span className="bg-brand-50 text-brand-700 grid size-10 shrink-0 place-items-center rounded-lg">
        <Glasses className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        {recommended ? <Badge tone="brand">{t('opticalOrders.recommendation.badge')}</Badge> : null}
        <p className="text-ink font-medium">
          {frame.productName}
          {frame.variantName ? ` — ${frame.variantName}` : ''}
        </p>
        <dl className="text-muted grid gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
          <div className="flex gap-1">
            <dt>{t('catalog.fields.sku')}:</dt>
            <dd className="font-mono" dir="ltr">
              {frame.sku}
            </dd>
          </div>
          <div className="flex gap-1">
            <dt>{t('catalog.fields.brand')}:</dt>
            <dd>{frame.brandName ?? t('catalog.fields.noBrand')}</dd>
          </div>
          <div className="flex gap-1">
            <dt>{t('catalog.fields.colorName')}:</dt>
            <dd>{frame.details?.colorName ?? t('catalog.fields.notSpecified')}</dd>
          </div>
          <div className="flex gap-1">
            <dt>{t('opticalOrders.frame.sizes')}:</dt>
            <dd className="font-mono" dir="ltr">
              {sizes ?? '—'}
            </dd>
          </div>
        </dl>
        {line ? <Availability line={line} /> : null}
        {recommendedUnavailable ? (
          <Alert tone="warning">{t('opticalOrders.recommendation.unavailableHere')}</Alert>
        ) : null}
      </div>
      <span className="text-ink font-semibold" dir="ltr">
        {price === null ? '—' : format(price)}
      </span>
    </section>
  );
}
